import { useState, useEffect } from "react";
import "./ResponseContentMultiple.css";
import { iconPath, Question } from "effno-core";
import { useSelector } from "react-redux";
import { selectEditingForm, selectUIStates } from "../../redux/features/appData";
import { ResponseNavigate } from "./ResponseNavigate";
import { MultipleResponse } from "../config/responseSettings";
import config from "environment";
import { ResponseErrors } from "./responseErrors";
import { ICONS } from "../shared/media";
import { NEXT_BUTTON } from "./ResponseContent";
import { CircleCheck, TriangleAlert } from "lucide-react";

const debugMode = config.debugMode;

type ResponseContentMultipleProps = {
    question: Question;
    responseValues: string;
    isPreview: boolean;
    isMobileLayout: boolean;
    isLastQuestion: boolean;
    commentMode: boolean;
    onNext?: () => void;
    onPrev?: () => void;
    onSubmit: (responseValues: string) => void;
}

export const ResponseContentMultiple = ({
    question,
    responseValues,
    isPreview,
    isMobileLayout,
    isLastQuestion,
    commentMode,
    onNext,
    onPrev,
    onSubmit
} : ResponseContentMultipleProps) => {

    // const editingForm = useSelector(selectEditingForm);
    // const uiStates = useSelector(selectUIStates);

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [settings, setSettings] = useState<any | undefined>(undefined);
    const [theChoices, setTheChoices] = useState<string[] | undefined>(undefined);
    const [choiceSelected, setChoiceSelected] = useState<boolean[]>([]);
    const [otherValue, setOtherValue] = useState<string | undefined>(undefined);

    const [error, setError] = useState<string>("");

    useEffect(() => {
        document.addEventListener("keydown", eventKeyDown);
        return () => {
            document.removeEventListener("keydown", eventKeyDown);
        }
    }, []);

    useEffect(() => {
        if (!question) {
            return;
        }

        setTitle(question.title);
        setDescription(question.description);

        const newSettings = JSON.parse(question.settings);
        setSettings(newSettings);
        setError(ResponseErrors.none);

        let finalChoices: string[] = [];

        // prep the choices
        if (newSettings.randomizeOn) {
            const choices = newSettings.choices;
            let currentIndex = choices.length
            let randomIndex;
            let tempValue;

            while (currentIndex !== 0) {
                randomIndex = Math.floor(Math.random() * currentIndex);
                currentIndex -= 1;

                tempValue = choices[currentIndex];
                choices[currentIndex] = choices[randomIndex];
                choices[randomIndex] = tempValue;
            }

            setTheChoices(choices);
            finalChoices = choices;
        } else {
            setTheChoices(newSettings.choices);
            finalChoices = newSettings.choices;
        }

        const responseChoices = JSON.parse(responseValues).choices || [];
        const selected = new Array(finalChoices.length).fill(false);

        finalChoices.forEach((choice: string, index: number) => {
            if (responseChoices.includes(choice)) {
                selected[index] = true;
            }
        })

        setChoiceSelected(selected);
    }, [question, responseValues])

    const eventKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            setOtherValue("");
        }
    }

    const metRequirements = (theChoicesSelected: boolean[]) : 
        "ok" | "required" | "tooFew" | "tooMany" => {

        if (!settings) {
            return "ok";
        }

        if (!question.required) {
            return "ok";
        }

        let count = 0;
        theChoicesSelected.forEach((choice: boolean) => {
            if (choice) {
                count++
            }
        });

        if (settings.hasOtherOn && otherValue) {
            count++;
        }

        if (question.required && count === 0) {
            return "required";
        }

        // min
        if (settings.enforceMinOn && count < settings.minChoices) {
            return "tooFew";  
        }

        if (settings.enforceMaxOn && count > settings.maxChoices) {
            return "tooMany";
        }
        
        return "ok";
    }

    const handleNext = () => {
        if (isPreview && onNext) {
            onNext();
        } else {
            handleSubmit(choiceSelected);
        }
    }

    const handleSelect = (index: number) => {

        if (!choiceSelected) {
            return;
        }

        const newChoiceSelected = [
            ...choiceSelected.slice(0, index), 
            !choiceSelected[index],
            ...choiceSelected.slice(index + 1)
        ];

        if (!settings.multiSelectOn) {
            for (let i = 0; i < newChoiceSelected.length; i++) {
                if (i !== index) {
                    newChoiceSelected[i] = false;
                }
            }

            // auto-select the choice
            setChoiceSelected(newChoiceSelected);
            handleSubmit(newChoiceSelected)
        }

        setChoiceSelected(newChoiceSelected);
    }

    const handleSubmit = (submitChoicesSelected: boolean[]) => {

        if (!theChoices) {
            return;
        }

        const result = metRequirements(submitChoicesSelected);
        
        if (result !== "ok") {
            // need to cast it here
            let errorMessage : string = ResponseErrors.none; 

            switch(result) {
                case "required":    
                    errorMessage = ResponseErrors.requireChoice;
                    break;
                case "tooFew":
                    errorMessage = `${ResponseErrors.tooFew} ${settings.minChoices} choice(s).`;
                    break;
                case "tooMany":
                    errorMessage = `${ResponseErrors.tooMany} ${settings.maxChoices} choice(s).`;
                    break;
                default:
                    break;
            }

            setError(errorMessage);
            return;
        }

        setError(ResponseErrors.none);
        
        const choices: string[] = [];

        for (let i = 0; i < settings?.choices.length; i++) {
            if (submitChoicesSelected && submitChoicesSelected.length > i && submitChoicesSelected[i]) {
                choices.push(theChoices[i]);
            }
        }

        if (settings.hasOtherOn && otherValue) {
            choices.push(otherValue);
        }

        const newResponseValues: string = JSON.stringify({
            choices 
        } as MultipleResponse);

        console.log("[ResponseContentMultiple] newResponseValues: ", newResponseValues);

        onSubmit(newResponseValues);
    }

    const requirementsMet = metRequirements(choiceSelected) === "ok";
    
    // console.log("[ResponseContentMultiple] question: ", question);
    // console.log("[ResponseContentMultiple] choiceSelected: ", choiceSelected);
    // console.log("[ResponseContentMultiple] theChoices: ", theChoices);

    return (
        <>
            <div className="form-content-body response-content-multiple-body">

                {/* {debugMode && question &&
                    <div className="response-content-question-id">
                        {question.id}
                    </div>
                } */}
                
                <div 
                    className="response-content-title"
                    data-testid={"response-content-title:" + title}
                >
                    {title}
                </div>
                
                <div className="response-content-description">
                {description}
                </div>   

                <div className="response-content-multiple">
                    <div className="response-content-multiple-options">
                        {theChoices && choiceSelected &&
                            theChoices.map((choice: string, index: number) => {
                                return (
                                    
                                    <div 
                                        className={choiceSelected[index] ? 
                                            "response-content-multiple-option-selected" :
                                            "response-content-multiple-option"
                                        }
                                        onClick={()=>handleSelect(index)}
                                        data-testid={"response-content-choice:" + choice}
                                    >
                                        <div className="response-content-multiple-option-value">
                                            {choice}
                                        </div>
                                        <div className="response-content-multiple-option-check">
                                            {choiceSelected[index] && 
                                                <CircleCheck size={20} color="var(--ftAnswerColor)"/>
                                                
                                                // <img 
                                                //     src={iconPath(ICONS.CHECK, undefined)}
                                                //     // alt="check"
                                                // />
                                            }
                                        </div>
                                    </div>
                                )
                            })
                        }
                        {settings && settings.hasOtherOn &&
                            <div 
                                className={otherValue ? 
                                    "response-content-multiple-other-selected" :
                                    "response-content-multiple-other"
                                }
                            >
                                <div className="response-content-multiple-other-value">  
                                    <input 
                                        type="text"
                                        value={otherValue}
                                        name="other"
                                        placeholder="Enter another choice"
                                        onChange={(e: any) => setOtherValue(e.target.value)}
                                        data-testid="response-content-other"
                                    />
                                </div>
                                <div className="response-content-multiple-option-check">
                                    {otherValue && 
                                        <CircleCheck size={20} color="var(--ftAnswerColor)"/>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>
                
                <div 
                    className="response-content-error"
                    style={!error ? {backgroundColor: "transparent"} : {}}
                    data-testid="response-content-error"
                >
                    {error && <TriangleAlert size={16}/>}{error}
                </div>

                {/* <div className="response-content-submit">
                    <button
                        className="response-content-button-submit"
                        onClick={()=>handleSubmit(choiceSelected)}
                        data-testid="response-content-submit"
                    >
                        {onNext ? NEXT_BUTTON : "Submit"}
                    </button>
                </div> */}
                {/* <div className="form-content-bottom-spacer">
                    &nbsp;
                </div> */}

                <ResponseNavigate 
                    isMobileLayout={isMobileLayout}
                    isLastQuestion={isLastQuestion}
                    commentMode={commentMode}
                    onSubmit={()=>handleSubmit(choiceSelected)}
                    onNext={handleNext}
                    onPrev={onPrev}
                />

            </div>
                
        </>
    )
}